import React from 'react';
import config from '../config';

const STORAGE_KEY = 'gallery.auth';
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000; // attempt refresh one minute before expiry

const initialState = {
  loading: !config.authDisabled,
  accessToken: config.authDisabled ? 'demo-token' : null,
  expiresAt: config.authDisabled ? Date.now() + 24 * 60 * 60 * 1000 : 0,
  username: null,
  scope: null,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'RESTORE':
      return { ...state, ...action.payload, loading: false, error: null };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        accessToken: action.payload.accessToken,
        expiresAt: action.payload.expiresAt,
        username: action.payload.username,
        scope: action.payload.scope,
        loading: false,
        error: null,
      };
    case 'LOGOUT':
      return { ...initialState, loading: false, accessToken: null, expiresAt: 0 };
    case 'SET_LOADING':
      return { ...state, loading: action.payload, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

const AuthContext = React.createContext(null);

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const refreshInFlight = React.useRef(null);

  const persistState = React.useCallback(
    (next) => {
      if (typeof window === 'undefined') {
        return;
      }
      if (config.authDisabled) {
        return;
      }
      try {
        if (next && next.accessToken) {
          const payload = {
            accessToken: next.accessToken,
            expiresAt: next.expiresAt,
            username: next.username,
            scope: next.scope,
          };
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } else {
          window.localStorage.removeItem(STORAGE_KEY);
        }
      } catch (err) {
        console.warn('Unable to persist auth state', err);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (config.authDisabled) {
      dispatch({ type: 'RESTORE', payload: initialState });
      return;
    }

    if (typeof window === 'undefined') {
      dispatch({ type: 'RESTORE', payload: { loading: false } });
      return;
    }

    let restored = null;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        restored = JSON.parse(raw);
      }
    } catch (err) {
      console.warn('Failed to read auth storage', err);
    }

    if (restored && restored.accessToken) {
      dispatch({ type: 'RESTORE', payload: { ...restored, loading: false } });
    } else {
      dispatch({ type: 'RESTORE', payload: { loading: false } });
    }
  }, []);

  const login = React.useCallback(async (credentials) => {
    if (config.authDisabled) {
      return;
    }
    const { username, password, scope } = credentials;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await fetch(`${config.authBaseUrl}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password, scope }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const message = body?.error || body?.error_description || 'Login failed';
        throw new Error(message);
      }
      const data = await response.json();
      const expiresAt = data?.expires_in ? Date.now() + data.expires_in * 1000 : Date.now() + 15 * 60 * 1000;
      const payload = {
        accessToken: data?.access_token || null,
        expiresAt,
        username,
        scope: data?.scope || scope || null,
      };
      dispatch({ type: 'LOGIN_SUCCESS', payload });
      persistState(payload);
      return payload;
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err.message || 'Login failed' });
      throw err;
    }
  }, [persistState]);

  const logout = React.useCallback(async () => {
    if (config.authDisabled) {
      dispatch({ type: 'LOGOUT' });
      return;
    }
    try {
      await fetch(`${config.authBaseUrl}/logout`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => undefined);
    } finally {
      dispatch({ type: 'LOGOUT' });
      persistState(null);
    }
  }, [persistState]);

  const refreshToken = React.useCallback(async () => {
    if (config.authDisabled) {
      return initialState.accessToken;
    }
    if (!refreshInFlight.current) {
      refreshInFlight.current = (async () => {
        const response = await fetch(`${config.authBaseUrl}/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('Refresh failed');
        }
        const data = await response.json();
        const expiresAt = data?.expires_in ? Date.now() + data.expires_in * 1000 : Date.now() + 15 * 60 * 1000;
        const payload = {
          accessToken: data?.access_token || null,
          expiresAt,
          username: state.username,
          scope: data?.scope || state.scope,
        };
        dispatch({ type: 'LOGIN_SUCCESS', payload });
        persistState(payload);
        return payload.accessToken;
      })()
        .catch((err) => {
          console.error('Token refresh failed', err);
          dispatch({ type: 'LOGOUT' });
          persistState(null);
          throw err;
        })
        .finally(() => {
          refreshInFlight.current = null;
        });
    }
    return refreshInFlight.current;
  }, [persistState, state.scope, state.username]);

  const ensureValidToken = React.useCallback(async () => {
    if (config.authDisabled) {
      return initialState.accessToken;
    }
    const now = Date.now();
    if (!state.accessToken) {
      throw new Error('Not authenticated');
    }
    if (state.expiresAt && state.expiresAt - TOKEN_REFRESH_BUFFER_MS > now) {
      return state.accessToken;
    }
    return refreshToken();
  }, [refreshToken, state.accessToken, state.expiresAt]);

  const fetchWithAuth = React.useCallback(
    async (input, init = {}) => {
      if (config.authDisabled) {
        return fetch(input, { ...init, credentials: 'include' });
      }
      const token = await ensureValidToken();
      const headers = new Headers(init.headers || {});
      headers.set('Authorization', `Bearer ${token}`);
      if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
      }

      const { timeoutMs, signal, ...rest } = init;
      let controller;
      let timeoutId;
      const timeout = typeof timeoutMs === 'number' ? timeoutMs : config.longRequestTimeoutMs;
      if (timeout && timeout > 0 && typeof AbortController !== 'undefined') {
        controller = new AbortController();
        if (signal) {
          if (signal.aborted) {
            controller.abort();
          } else {
            signal.addEventListener('abort', () => controller.abort(), { once: true });
          }
        }
        timeoutId = window.setTimeout(() => controller.abort(), timeout);
      }

      try {
        return await fetch(input, {
          ...rest,
          headers,
          credentials: 'include',
          signal: controller ? controller.signal : signal,
        });
      } finally {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      }
    },
    [ensureValidToken],
  );

  const value = React.useMemo(
    () => ({
      ...state,
      isAuthenticated: !!state.accessToken,
      login,
      logout,
      fetchWithAuth,
      ensureValidToken,
    }),
    [fetchWithAuth, ensureValidToken, login, logout, state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
